import React, { Suspense, useRef, useEffect, useCallback, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

function Model({ 
  url, 
  position, 
  rotation, 
  scale, 
  onLoad, 
  onError,
  onSelect,
  isSelected,
  modelId 
}) {
  const { scene, nodes, materials } = useGLTF(url);
  const modelRef = useRef();
  const itemRootRef = useRef();
  const [isNormalized, setIsNormalized] = useState(false);
  const [initialTransform, setInitialTransform] = useState(null);

  // Apply transforms from props immediately when they change (for saved outfits)
  useEffect(() => {
    if (itemRootRef.current && isNormalized) {
      console.log(`[ModelLoader] Applying updated transforms from props:`, { position, rotation, scale });
      itemRootRef.current.position.set(...position);
      itemRootRef.current.rotation.set(...rotation);
      itemRootRef.current.scale.set(...scale);
    }
  }, [position, rotation, scale, isNormalized]);

  // Normalize and center the model ONLY ONCE after loading
  useEffect(() => {
    if (scene && itemRootRef.current && !isNormalized) {
      console.log(`[ModelLoader] Normalizing model ${modelId} - this should only happen once`);
      console.log(`[ModelLoader] Received transforms - position: ${position}, rotation: ${rotation}, scale: ${scale}`);
      
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      
      // Check if we have explicit transforms (from saved outfit) or need to use defaults
      const hasExplicitTransforms = !(position[0] === 0 && position[1] === 0 && position[2] === 0 && 
                                     rotation[0] === 0 && rotation[1] === 0 && rotation[2] === 0 && 
                                     scale[0] === 1 && scale[1] === 1 && scale[2] === 1);
      
      console.log(`[ModelLoader] Has explicit transforms: ${hasExplicitTransforms}`);
      
      // Store the initial transform data
      const initialData = {
        position: hasExplicitTransforms ? position : [0, 0, 0],
        rotation: hasExplicitTransforms ? rotation : [0, 0, 0],
        scale: hasExplicitTransforms ? scale : [1, 1, 1],
        boundingBox: box,
        center: center,
        size: size
      };
      setInitialTransform(initialData);
      
      // Center the model at origin (this is the model's internal structure)
      scene.position.sub(center);
      
      // Calculate appropriate scale to fit in reasonable bounds
      const maxDimension = Math.max(size.x, size.y, size.z);
      const targetSize = 2; // Target size in units
      const scaleFactor = targetSize / maxDimension;
      
      // Apply initial scale to the model
      scene.scale.setScalar(scaleFactor);
      
      // Position the wrapper group using the stored initial transform
      console.log(`[ModelLoader] Applying transforms to wrapper group:`, initialData);
      itemRootRef.current.position.set(...initialData.position);
      itemRootRef.current.rotation.set(...initialData.rotation);
      itemRootRef.current.scale.set(...initialData.scale);
      
      // Mark as normalized to prevent re-running
      setIsNormalized(true);
      
      if (onLoad) {
        onLoad(scene, {
          boundingBox: box,
          center: center,
          size: size,
          normalizedScale: scaleFactor,
          initialTransform: initialData
        });
      }
    }
  }, [scene, onLoad, modelId, isNormalized]); // Remove position, rotation, scale from dependencies to prevent re-normalization

  // Handle model selection
  const handleClick = useCallback((event) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect(modelId, itemRootRef.current);
    }
  }, [onSelect, modelId]);

  // Handle pointer events for selection
  const handlePointerDown = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handlePointerOver = useCallback((event) => {
    event.stopPropagation();
    document.body.style.cursor = 'pointer';
  }, []);

  const handlePointerOut = useCallback((event) => {
    event.stopPropagation();
    document.body.style.cursor = 'auto';
  }, []);

  // Clone the scene to avoid conflicts
  const clonedScene = scene.clone();

  // Ensure materials maintain their original properties and make meshes selectable
  clonedScene.traverse((child) => {
    if (child.isMesh && child.material) {
      // Make mesh selectable
      child.userData.selectable = true;
      child.userData.modelId = modelId;
      
      // Preserve original material properties exactly as they are
      // DO NOT modify colors, roughness, metalness, transparency, or any other properties
      if (Array.isArray(child.material)) {
        child.material.forEach(mat => {
          if (mat) {
            // Only mark material as needing update without changing any properties
            mat.needsUpdate = true;
            // Keep original transparency, opacity, colors, metalness, roughness, etc.
          }
        });
      } else if (child.material) {
        // Only mark material as needing update without changing any properties
        child.material.needsUpdate = true;
        // Keep original transparency, opacity, colors, metalness, roughness, etc.
      }
    }
  });

  // CRITICAL: Only run this once after normalization, never on every frame
  useFrame(() => {
    // Remove any animation logic that could interfere with transforms
    // The model should stay exactly where the user puts it
  });

  return (
    <group 
      ref={itemRootRef}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      userData={{ 
        type: 'modelWrapper', 
        modelId: modelId,
        selectable: true,
        isNormalized: isNormalized
      }}
    >
      <primitive
        ref={modelRef}
        object={clonedScene}
      />
    </group>
  );
}

// Error boundary for model loading
function ModelErrorBoundary({ children, fallback }) {
  return (
    <Suspense fallback={fallback}>
      {children}
    </Suspense>
  );
}

// Loading fallback component
function ModelLoadingFallback() {
  return (
    <group>
      <mesh>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="#666666" />
      </mesh>
      <mesh position={[0, 0.8, 0]}>
        <sphereGeometry args={[0.1, 8, 6]} />
        <meshStandardMaterial color="#666666" />
      </mesh>
    </group>
  );
}

// Main ModelLoader component
export default function ModelLoader({ 
  modelUrl, 
  position = [0, 0, 0], 
  rotation = [0, 0, 0], 
  scale = [1, 1, 1],
  onLoad,
  onError,
  onSelect,
  isSelected,
  modelId
}) {
  if (!modelUrl) {
    return null;
  }

  return (
    <ModelErrorBoundary fallback={<ModelLoadingFallback />}>
      <Model
        url={modelUrl}
        position={position}
        rotation={rotation}
        scale={scale}
        onLoad={onLoad}
        onError={onError}
        onSelect={onSelect}
        isSelected={isSelected}
        modelId={modelId}
      />
    </ModelErrorBoundary>
  );
}

// Preload models for better performance
export function preloadModel(url) {
  if (typeof window !== 'undefined') {
    useGLTF.preload(url);
  }
}
